import { useCallback, useEffect, useRef, useState } from "react";
import { Badge, Banner, BlockStack, Button, Card, InlineStack, Modal, Pagination, Text } from './admin-ui';
import { RefreshIcon } from './admin-ui/icons';
import { q_upsell_failures } from "../api";
import { requireSuccess, upsellError } from "../utils/upsellAdmin";

const stages = {
  payment: "Airwallex payment",
  payment_verification: "Payment verification",
  pending: "Preparing order update",
  begin: "Opening order edit",
  add_variant: "Adding product",
  ready_to_commit: "Saving order edit",
  commit_pending: "Saving order edit (verify result)",
  committed: "Marking order as paid",
};
const date = (value) => Number(value) > 0 ? new Date(Number(value) * 1000).toLocaleString() : "—";
const money = (item) => {
  const amount = Number(item.amount);
  return `${item.currency_code || ""} ${Number.isFinite(amount) ? amount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 4 }) : item.amount || "—"}`;
};

export function useUpsellFailures() {
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ list: [], total: 0, last_page: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const sequence = useRef(0);
  const inFlight = useRef(false);
  const load = useCallback(async () => {
    const request = ++sequence.current;
    inFlight.current = true;
    setLoading(true);
    try {
      const next = requireSuccess(await q_upsell_failures({ page, limit: 20 }));
      if (!Array.isArray(next?.list)) throw new Error("Could not load upsell failures.");
      if (request !== sequence.current) return;
      if (page > Math.max(1, Number(next.last_page))) {
        setPage(Math.max(1, Number(next.last_page)));
        return;
      }
      setData(next);
      setError("");
    } catch (e) {
      if (request === sequence.current) setError(upsellError(e));
    } finally {
      if (request === sequence.current) { setLoading(false); inFlight.current = false; }
    }
  }, [page]);

  useEffect(() => {
    const requests = sequence;
    load();
    const timer = setInterval(() => { if (!inFlight.current) load(); }, 30000);
    return () => { clearInterval(timer); requests.current++; };
  }, [load]);

  return { data, page, setPage, loading, error, load };
}

function FailuresView({ controller }) {
  const { data, page, setPage, loading, error, load } = controller;
  const [selected, setSelected] = useState(null);
  return <Card>
    <BlockStack gap="400">
      <InlineStack align="space-between" blockAlign="center">
        <div><Text as="h2" variant="headingMd">Orders requiring attention</Text>
          <p className="upsell-muted">Payment and order update issues that need manual review.</p></div>
        <Button icon={RefreshIcon} onClick={load} loading={loading}>Refresh</Button>
      </InlineStack>
      {error && <Banner tone="critical">{error}</Banner>}
      {Number(data.total) > 0 ? <>
        <Banner tone="warning">
          Automatic processing has stopped. Review these orders and payments manually.
          No automatic refund or further order update will be attempted.
        </Banner>
        <div className="upsell-table-wrap" tabIndex={0} role="region" aria-label="Upsell failures" aria-busy={loading}>
          <table className="upsell-failures-table">
            <thead><tr>{["Order", "Product", "Amount", "Payment", "Failed step", "Update attempts", "Details"].map((heading) => <th key={heading} scope="col">{heading}</th>)}</tr></thead>
            <tbody>{data.list.map((item) => <tr key={item.id}>
              <td><strong>{item.order_name || `Order ${item.order_id}`}</strong></td>
              <td><span className="upsell-table-product">{item.product_title || "Product"}</span></td>
              <td className="upsell-nowrap">{money(item)}</td>
              <td><Badge tone={Number(item.paid_at) > 0 ? "success" : "attention"}>{Number(item.paid_at) > 0 ? "Payment received" : "Verify payment"}</Badge></td>
              <td><Badge tone={Number(item.paid_at) > 0 ? "critical" : "attention"}>{stages[item.sync_stage] || item.sync_stage || "Order update failed"}</Badge></td>
              <td>{Number(item.sync_attempts) || 0}</td>
              <td><Button variant="plain" accessibilityLabel={`View details for ${item.order_name || item.order_id}`} onClick={() => setSelected(item)}>View details</Button></td>
            </tr>)}</tbody>
          </table>
        </div>
        <InlineStack align="space-between" blockAlign="center">
          <Text as="p" tone="subdued">{`Showing ${(page - 1) * 20 + 1}–${(page - 1) * 20 + data.list.length} of ${data.total} records`}</Text>
          <Pagination hasPrevious={page > 1 && !loading} hasNext={page < Number(data.last_page) && !loading}
            onPrevious={() => setPage((value) => value - 1)} onNext={() => setPage((value) => value + 1)} />
        </InlineStack>
      </> : !error && <div className="upsell-empty">
        <span className="upsell-empty-check" aria-hidden="true">✓</span>
        <Text as="h3" variant="headingMd">{loading ? "Loading records…" : "No orders need attention"}</Text>
        <p className="upsell-muted">{loading ? "Checking payment and order updates." : "Failed upsell order updates will appear here."}</p>
      </div>}
    </BlockStack>
    <Modal open={Boolean(selected)} onClose={() => setSelected(null)} title={`Upsell issue · ${selected?.order_name || selected?.order_id || ""}`}
      secondaryActions={[{ content: "Close", onAction: () => setSelected(null) }]}>
      <Modal.Section>{selected && <BlockStack gap="400">
        <Banner tone={Number(selected.paid_at) > 0 ? "critical" : "warning"}>
          {Number(selected.paid_at) > 0 ? "Payment was received. Verify the Shopify order before making any changes." : "Payment is not confirmed. Verify the payment result before updating the order. Do not charge again."}
        </Banner>
        <dl className="upsell-detail-list">
          <dt>Product</dt><dd>{selected.product_title || "Product"}</dd>
          <dt>Variant ID</dt><dd>{selected.variant_id}</dd>
          <dt>Amount</dt><dd>{money(selected)}</dd>
          <dt>Failed step</dt><dd>{stages[selected.sync_stage] || selected.sync_stage}</dd>
          <dt>Order update attempts</dt><dd>{Number(selected.sync_attempts) || 0}</dd>
          <dt>Payment received at</dt><dd>{date(selected.paid_at)}</dd>
          <dt>Failed at</dt><dd>{date(selected.failed_at)}</dd>
          <dt>Record ID</dt><dd>{selected.id}</dd>
        </dl>
        <div><Text as="h3" variant="headingSm">Failure reason</Text><p className="upsell-failure-reason">{selected.last_error || "No additional details available."}</p></div>
      </BlockStack>}</Modal.Section>
    </Modal>
  </Card>;
}

function StandaloneFailures() {
  const controller = useUpsellFailures();
  return <FailuresView controller={controller} />;
}

export default function UpsellFailures({ controller } = {}) {
  return controller ? <FailuresView controller={controller} /> : <StandaloneFailures />;
}
